import React from "react";
import { Icon } from "./siIcons";
import { siApi } from "./siApi";

// Statistiques des sauvegardes Singapour (onglet Sauvegardes) : rapports
// quotidiens du script de surveillance GCP, relevés dans la boîte dev@ par le
// serveur. Composé avec les éléments de l'écran S.I (tuiles .sys-stats,
// pastilles .si-st, tableau .mdt-t) ; graphique en SVG.

const fmtJour = (j) => (j ? j.split("-").reverse().join("/") : "—");
const fmtDateHeure = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Indian/Reunion", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const pluriel = (n, m) => `${n} ${m}${n > 1 ? "s" : ""}`;

function Graphique({ jours }) {
  const L = 14, G = 4, H = 64;
  const w = jours.length * (L + G);
  return (
    <svg className="six-bk-graph" viewBox={`0 0 ${w} ${H + 4}`} role="img" aria-label="Conformité des sauvegardes sur 30 jours" preserveAspectRatio="none">
      {jours.map((j, i) => {
        const x = i * (L + G);
        const titre = `${fmtJour(j.jour)} : ${j.bilan ? `${j.bilan === "OK" ? "conforme" : "alerte"}, ${j.conformes}/${j.total} disques` : "aucun rapport"}`;
        if (!j.bilan) return <rect key={j.jour} x={x} y={2} width={L} height={H} rx="3" className="six-bk-vide"><title>{titre}</title></rect>;
        const h = Math.max(6, Math.round((j.conformes / Math.max(j.total, 1)) * H));
        return <rect key={j.jour} x={x} y={2 + H - h} width={L} height={h} rx="3" className={j.bilan === "OK" ? "six-bk-ok" : "six-bk-alerte"}><title>{titre}</title></rect>;
      })}
    </svg>
  );
}

/* ---------- Squelettes (à la forme du relevé) ---------- */

const LARGEURS = [96, 54, 128, 64, 104, 80];
// Barres du graphique 30 jours : presque toutes pleines, comme un mois réel.
const HAUTEURS = [100, 100, 100, 92, 100, 100, 100, 100, 72, 100, 100, 100, 100, 100, 100, 100, 86, 100, 100, 100, 100, 100, 100, 100, 100, 64, 100, 100, 100, 100];

// Tableau : en-têtes réels quand on les connaît, sinon barres.
export function TableSquelette({ colonnes, lignes = 3 }) {
  const cols = Array.isArray(colonnes) ? colonnes : Array.from({ length: colonnes }, () => null);
  return (
    <div className="mdt-wrap sys-tw" aria-hidden="true">
      <table className="mdt-t sys-t">
        <thead>
          <tr>{cols.map((c, i) => <th key={i}>{c ?? <span className="six-sk" style={{ width: 72, height: 8 }} />}</th>)}</tr>
        </thead>
        <tbody>
          {Array.from({ length: lignes }, (_, r) => (
            <tr key={r}>
              {cols.map((_, i) => <td key={i}><span className="six-sk" style={{ width: LARGEURS[(r + i) % LARGEURS.length] }} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SqueletteSauvegardes() {
  return (
    <div className="six-bk" aria-hidden="true">
      <div className="six-bk-head">
        <span className="six-sk" style={{ width: 70, height: 18, borderRadius: 9 }} />
        <span className="six-sk" style={{ width: 150, height: 12 }} />
        <span className="six-sk" style={{ width: 260 }} />
        <span className="six-bk-act"><span className="six-sk" style={{ width: 80, height: 26, borderRadius: 6 }} /></span>
      </div>
      <div className="sys-stats">
        {[56, 48, 32, 24, 52].map((w, i) => (
          <div key={i}>
            <b><span className="six-sk" style={{ width: w, height: 18 }} /></b>
            <span><span className="six-sk" style={{ width: 118 }} /></span>
            <small className="six-sk" style={{ width: 84 }} />
          </div>
        ))}
      </div>
      <div className="six-bk-bloc">
        <div className="six-bk-titre">
          <span className="six-sk" style={{ width: 110 }} />
          <span className="six-bk-leg"><span className="six-sk" style={{ width: 190 }} /></span>
        </div>
        <div className="six-bk-sk-graph">{HAUTEURS.map((h, i) => <span key={i} className="six-sk" style={{ height: `${h}%` }} />)}</div>
      </div>
      <TableSquelette colonnes={["Disque", "Statut", "Dernier READY à Singapour", "Jours conformes (30 j)", "Anomalie"]} lignes={4} />
      <span className="six-sk" style={{ width: 230 }} />
    </div>
  );
}

/* ---------- Relevé ---------- */

// `initial` ({ donnees } ou { erreur }) : relevé déjà chargé par l'onglet,
// pour que tout s'affiche d'un coup ; sans lui, le composant le charge.
export default function SiSauvegardesStats({ initial = null }) {
  const [d, setD] = React.useState(initial?.donnees ?? null);
  const [erreur, setErreur] = React.useState(initial?.erreur ?? null);
  const [actualise, setActualise] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const [histo, setHisto] = React.useState(false);

  React.useEffect(() => {
    if (initial) return undefined;
    let annule = false;
    siApi.sauvegardes().then((x) => !annule && setD(x)).catch((e) => !annule && setErreur(e.message));
    return () => { annule = true; };
  }, [initial]);

  const actualiser = async () => {
    setActualise(true);
    setErreur(null);
    setInfo(null);
    try {
      const r = await siApi.releverSauvegardes();
      setD(r);
      setInfo(r.releve && r.releve.importes ? `${pluriel(r.releve.importes, "rapport")} importé${r.releve.importes > 1 ? "s" : ""}.` : "Aucun nouveau rapport.");
    } catch (e) {
      setErreur(e.message);
    } finally {
      setActualise(false);
    }
  };

  if (!d && !erreur) return <SqueletteSauvegardes />;
  if (!d) return <div className="six-bk"><div className="six-bk-msg is-err">Relevé indisponible : {erreur}</div></div>;

  const { dernier, stats } = d;
  const bouton = (
    <button className="sys-mini" onClick={actualiser} disabled={actualise}>{actualise ? "Relevé en cours…" : "Actualiser"}</button>
  );
  if (!dernier) {
    return (
      <div className="six-bk">
        <div className="six-bk-head"><b>Sauvegardes Singapour</b>{d.configure && bouton}</div>
        <div className="six-bk-msg">
          {d.configure ? "Aucun rapport enregistré pour l'instant. Cliquez sur « Actualiser » pour relever la boîte dev@." : "La boîte dev@ n'est pas encore connectée à MyALFA : les rapports quotidiens ne sont pas relevés."}
        </div>
        {erreur && <div className="six-bk-msg is-err">{erreur}</div>}
      </div>
    );
  }
  const alerte = dernier.bilan === "ALERTE";
  return (
    <div className="six-bk">
      <div className="six-bk-head">
        <span className={`si-st ${alerte ? "is-out" : "is-on"}`}>{alerte ? "Alerte" : "Conforme"}</span>
        <b>Sauvegardes Singapour</b>
        <span className="six-bk-sub">contrôle du {fmtDateHeure(dernier.dateControle)} · {dernier.conformes}/{dernier.total} disques conformes</span>
        <span className="six-bk-act">{info && <small>{info}</small>}{bouton}</span>
      </div>
      {d.retard && <div className="six-bk-msg is-warn"><Icon name="info" size={13} /> Aucun rapport reçu aujourd'hui : le contrôle de ce matin n'est pas arrivé dans la boîte dev@.</div>}
      {erreur && <div className="six-bk-msg is-err">{erreur}</div>}

      <div className="sys-stats">
        <div className={alerte ? "is-warn" : ""}><b>{dernier.conformes}/{dernier.total}</b><span>Disques conformes</span><small>{dernier.attendus ? `${dernier.attendus} attendus` : "dernier contrôle"}</small></div>
        <div><b>{stats.taux === null ? "—" : `${stats.taux} %`}</b><span>Conformité sur 30 jours</span><small>{stats.joursOk} jour{stats.joursOk > 1 ? "s" : ""} conforme{stats.joursOk > 1 ? "s" : ""}{stats.joursAlerte ? ` · ${pluriel(stats.joursAlerte, "alerte")}` : ""}</small></div>
        <div><b>{stats.serie}</b><span>Jours conformes d'affilée</span><small>{stats.joursSansRapport ? `${pluriel(stats.joursSansRapport, "jour")} sans rapport` : "aucun rapport manquant"}</small></div>
        <div className={dernier.anomalies.length ? "is-warn" : ""}><b>{dernier.anomalies.length}</b><span>Anomalie{dernier.anomalies.length > 1 ? "s" : ""} en cours</span><small>{dernier.anomalies.length ? dernier.anomalies.map((a) => a.disque).join(" · ") : "aucune"}</small></div>
        <div><b>{stats.derniereReady ? fmtDateHeure(stats.derniereReady).split(" ")[1] : "—"}</b><span>Dernier snapshot READY</span><small>{stats.derniereReady ? fmtDateHeure(stats.derniereReady).split(" ")[0] : "aucun"}</small></div>
      </div>

      <div className="six-bk-bloc">
        <div className="six-bk-titre">30 derniers jours <span className="six-bk-leg"><i className="ok"></i>conforme<i className="alerte"></i>alerte<i className="vide"></i>sans rapport</span></div>
        <Graphique jours={d.jours} />
      </div>

      <div className="mdt-wrap sys-tw">
        <table className="mdt-t sys-t">
          <thead><tr><th>Disque</th><th>Statut</th><th>Dernier READY à Singapour</th><th className="r">Jours conformes (30 j)</th><th>Anomalie</th></tr></thead>
          <tbody>
            {d.disques.map((x) => (
              <tr key={x.nom}>
                <td><span className="six-bk-mono">{x.nom}</span></td>
                <td><span className={`si-st ${x.statut === "OK" ? "is-on" : "is-out"}`}>{x.statut === "OK" ? "OK" : "Alerte"}</span></td>
                <td>{x.dernierReady ? fmtDateHeure(x.dernierReady) : "aucun"}</td>
                <td className="r">{x.joursSuivis ? `${x.joursConformes}/${x.joursSuivis}` : "—"}</td>
                <td>{x.statut === "OK" ? "" : x.anomalie || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="mf-link" onClick={() => setHisto(!histo)}>{histo ? "Masquer" : "Afficher"} l'historique des rapports ({d.historique.length})</button>
      {histo && (
        <div className="mdt-wrap sys-tw">
          <table className="mdt-t sys-t">
            <thead><tr><th>Contrôle</th><th>Bilan</th><th className="r">Disques conformes</th><th>Anomalies</th></tr></thead>
            <tbody>
              {d.historique.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDateHeure(r.dateControle)}</td>
                  <td><span className={`si-st ${r.bilan === "OK" ? "is-on" : "is-out"}`}>{r.bilan === "OK" ? "Conforme" : "Alerte"}</span></td>
                  <td className="r">{r.conformes}/{r.total}</td>
                  <td>{r.anomalies.length ? r.anomalies.map((a) => a.disque).join(", ") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="six-bk-note">Ce contrôle vérifie la présence des snapshots ; il ne remplace pas un test de restauration des données ACD.</div>
    </div>
  );
}
